import { BigNumber } from '@ethersproject/bignumber'

import { isTxReceiptDebugEnabled, txReceiptDebug } from '../../utils/debugTxReceipt'
import { SerializableTransactionReceipt } from './actions'

/** ethers v5 no acepta Number.MAX_SAFE_INTEGER en `from(number)` — usar string decimal. */
const MAX_SAFE = BigNumber.from(String(Number.MAX_SAFE_INTEGER))

function safeHexToNumber(hex: string | undefined): number {
  if (!hex || hex === '0x') return 0
  try {
    const bn = BigNumber.from(hex)
    if (bn.gt(MAX_SAFE)) {
      return Number.MAX_SAFE_INTEGER
    }
    return bn.toNumber()
  } catch {
    return 0
  }
}

/**
 * EIP-658: status 0 = fallo, 1 = éxito. Algunos nodos devuelven valores fuera de rango;
 * si hay receipt minado y el valor no es 0, tratamos como éxito para la UI.
 */
function parseStatusHexResult(hex: string | undefined): {
  status: number | undefined
  anomaly: string | null
} {
  if (hex === undefined || hex === null || hex === '0x') return { status: undefined, anomaly: null }
  try {
    const bn = BigNumber.from(hex)
    if (bn.isZero()) return { status: 0, anomaly: null }
    if (bn.lte(1)) return { status: bn.toNumber(), anomaly: null }
    return { status: 1, anomaly: 'eip658_status_out_of_range_treated_as_success' }
  } catch {
    return { status: 1, anomaly: 'status_hex_invalid_treated_as_success' }
  }
}

/** Parsea la respuesta JSON de `eth_getTransactionReceipt` sin pasar por el formatter de ethers. */
export function parseRawTransactionReceipt(raw: unknown): SerializableTransactionReceipt | null {
  if (!raw || typeof raw !== 'object') return null
  const r = raw as Record<string, unknown>
  const th = r.transactionHash
  if (typeof th !== 'string' || !th.startsWith('0x')) return null

  const blockNumberHex = typeof r.blockNumber === 'string' ? r.blockNumber : undefined
  const txIndexHex = typeof r.transactionIndex === 'string' ? r.transactionIndex : undefined
  const statusHex = typeof r.status === 'string' ? r.status : undefined
  const { status, anomaly } = parseStatusHexResult(statusHex)

  if (anomaly && isTxReceiptDebugEnabled()) {
    txReceiptDebug('receipt RPC status anómalo normalizado', {
      transactionHash: th,
      statusHex,
      parsedStatus: status,
      anomaly
    })
  }

  return {
    blockHash: typeof r.blockHash === 'string' ? r.blockHash : '',
    blockNumber: safeHexToNumber(blockNumberHex),
    contractAddress:
      typeof r.contractAddress === 'string' && r.contractAddress.startsWith('0x') ? r.contractAddress : '',
    from: typeof r.from === 'string' ? r.from : '',
    status,
    to: typeof r.to === 'string' ? r.to : '',
    transactionHash: th,
    transactionIndex: safeHexToNumber(txIndexHex)
  }
}

type ProviderLike = {
  send(method: string, params: unknown[]): Promise<unknown>
}

/**
 * Una única llamada RPC `eth_getTransactionReceipt` sin parsear con ethers:
 * evita NUMERIC_FAULT en nodos que devuelven campos fuera del rango que espera ethers v5.
 */
export async function fetchSerializableReceipt(
  provider: ProviderLike | null | undefined,
  hash: string
): Promise<SerializableTransactionReceipt | null> {
  if (!provider || typeof provider.send !== 'function') return null

  try {
    const raw = await provider.send('eth_getTransactionReceipt', [hash])
    return parseRawTransactionReceipt(raw)
  } catch {
    return null
  }
}
